import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Receipt, RefreshCw, Loader2, Lock, Download, Ban, FileSpreadsheet, FileText,
  ChevronRight, CalendarRange,
} from 'lucide-react';
import API_BASE from '../../utils/apiBase';
import HelpSectionHint from '../../components/portal/HelpSectionHint';

const API = API_BASE;

const STATUS_STYLES = {
  draft: 'bg-slate-50 text-slate-700 border-slate-200',
  locked: 'bg-amber-50 text-amber-800 border-amber-200',
  exported: 'bg-green-50 text-green-700 border-green-200',
  voided: 'bg-red-50 text-red-700 border-red-200',
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const toLocalInputValue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fromLocalInputValue = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

export default function TimesheetsPage() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [generateNotes, setGenerateNotes] = useState('');

  const fetchTimesheets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      const url = `${API}/timesheets${params.toString() ? `?${params}` : ''}`;
      const res = await axios.get(url, { headers });
      setTimesheets(res.data?.timesheets || []);
    } catch {
      toast.error('Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter]);

  const applyPeriodPreset = useCallback(async (preset) => {
    try {
      const res = await axios.get(
        `${API}/timesheets/suggested-period?preset=${preset}`,
        { headers },
      );
      const start = res.data?.pay_period_start;
      const end = res.data?.pay_period_end;
      if (start) setPeriodStart(toLocalInputValue(start));
      if (end) setPeriodEnd(toLocalInputValue(end));
      return res.data;
    } catch {
      toast.error('Could not load pay period');
      return null;
    }
  }, [headers]);

  const loadSuggestedPeriod = useCallback(async () => {
    await applyPeriodPreset('last_week');
  }, [applyPeriodPreset]);

  const fetchSummary = useCallback(async () => {
    const startIso = fromLocalInputValue(periodStart);
    const endIso = fromLocalInputValue(periodEnd);
    if (!startIso || !endIso) {
      setSummary(null);
      return;
    }
    try {
      const params = new URLSearchParams({
        pay_period_start: startIso,
        pay_period_end: endIso,
      });
      const res = await axios.get(`${API}/timesheets/summary?${params}`, { headers });
      setSummary(res.data);
    } catch {
      setSummary(null);
    }
  }, [headers, periodStart, periodEnd]);

  useEffect(() => {
    fetchTimesheets();
    loadSuggestedPeriod();
  }, [fetchTimesheets, loadSuggestedPeriod]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const fetchDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/timesheets/${id}`, { headers });
      setDetail(res.data?.timesheet || null);
      setSelectedId(id);
    } catch {
      toast.error('Failed to load timesheet detail');
    } finally {
      setDetailLoading(false);
    }
  }, [headers]);

  const handleGenerate = async () => {
    const startIso = fromLocalInputValue(periodStart);
    const endIso = fromLocalInputValue(periodEnd);
    if (!startIso || !endIso) {
      toast.error('Set pay period start and end');
      return;
    }
    const unprocessed = summary?.unprocessed_by_employee?.length || 0;
    const msg = unprocessed
      ? `Create draft timesheets for ${unprocessed} worker(s) in this period?`
      : 'Generate timesheets for approved attendance in this period?';
    if (!window.confirm(msg)) return;

    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/timesheets/generate`,
        {
          pay_period_start: startIso,
          pay_period_end: endIso,
          notes: generateNotes.trim() || null,
        },
        { headers },
      );
      const count = res.data?.total || 0;
      toast.success(count ? `Created ${count} timesheet(s)` : 'No timesheets created');
      await fetchTimesheets();
      await fetchSummary();
      if (res.data?.timesheets?.[0]?.id) {
        fetchDetail(res.data.timesheets[0].id);
      }
    } catch (err) {
      showErrorToast(err, 'Could not generate timesheets');
    } finally {
      setActionLoading(false);
    }
  };

  const runAction = async (path, successMessage) => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/timesheets/${selectedId}${path}`, {}, { headers });
      toast.success(successMessage);
      await fetchTimesheets();
      await fetchDetail(selectedId);
      await fetchSummary();
    } catch (err) {
      const detailMsg = err.response?.data?.detail || 'Action failed';
      toast.error(typeof detailMsg === 'string' ? detailMsg : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLock = () => {
    if (!window.confirm('Lock this timesheet? Attendance lines cannot be changed after lock.')) return;
    runAction('/lock', 'Timesheet locked');
  };

  const handleVoid = () => {
    if (!window.confirm('Void this timesheet and release attendance back to unprocessed?')) return;
    runAction('/void', 'Timesheet voided');
  };

  const handleExport = async (format = 'csv') => {
    if (!selectedId) return;
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${API}/timesheets/${selectedId}/export`,
        { format },
        { headers, responseType: 'blob' },
      );
      const ref = detail?.reference || selectedId;
      const isPdf = format === 'pdf';
      const blob = new Blob([res.data], { type: isPdf ? 'application/pdf' : 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${ref}.${isPdf ? 'pdf' : 'csv'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(isPdf ? 'Timesheet PDF downloaded' : 'Timesheet CSV downloaded');
      await fetchTimesheets();
      await fetchDetail(selectedId);
    } catch (err) {
      const detailMsg = err.response?.data?.detail || 'Export failed';
      toast.error(typeof detailMsg === 'string' ? detailMsg : 'Export failed');
    } finally {
      setActionLoading(false);
    }
  };

  const unprocessedCount = summary?.unprocessed_by_employee?.length || 0;
  const unprocessedHours = (summary?.unprocessed_by_employee || []).reduce(
    (sum, row) => sum + (row.unprocessed_hours || 0),
    0,
  );
  const diag = summary?.diagnostics;

  const formatApiError = (err, fallback) => {
    if (!err?.response) {
      return err?.message || `${fallback} — no response from server (check network or API URL)`;
    }
    const data = err.response?.data;
    const detail = data?.detail ?? data?.message;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
    }
    if (detail && typeof detail === 'object') {
      if (detail.message) {
        const hints = detail.hints || {};
        const parts = [detail.message];
        if (hints.approved_total > 0 && hints.eligible_in_period === 0) {
          parts.push(
            `${hints.approved_total} approved overall — ${hints.outside_pay_period || 0} outside this period, `
            + `${hints.already_on_timesheet || 0} already on a timesheet.`,
          );
        }
        return parts.join(' ');
      }
      return JSON.stringify(detail);
    }
    if (typeof data === 'string' && data.trim()) return data;
    return `${fallback} (HTTP ${err.response.status})`;
  };

  const showErrorToast = (err, fallback) => {
    const description = formatApiError(err, fallback);
    toast.error(fallback, { description, duration: 12000 });
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-text-primary flex items-center gap-2">
            <Receipt className="h-7 w-7 text-primary" />
            Timesheets
          </h1>
          <p className="text-sm text-text-muted mt-1">
            Bundle approved hours for worker pay (part of the predictable financial pipeline). Shifts approve → generate+lock here → feeds Payroll (pay) + Invoices (bill) in parallel → Financially Closed records appear in Shifts archive.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchTimesheets} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card data-tour="timesheets-pay-period" data-help-section="pay-period">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarRange className="h-5 w-5" />
            Pay period (weekly)
            <HelpSectionHint sectionId="pay-period" />
          </CardTitle>
          <p className="text-sm text-text-muted">
            Only approved attendance with payable times in this range and not yet on a timesheet will be included.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <Label htmlFor="period-start">Period start</Label>
              <Input
                id="period-start"
                type="datetime-local"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="period-end">Period end</Label>
              <Input
                id="period-end"
                type="datetime-local"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="gen-notes">Notes (optional)</Label>
              <Input
                id="gen-notes"
                value={generateNotes}
                onChange={(e) => setGenerateNotes(e.target.value)}
                placeholder="e.g. Week 22 payroll"
              />
            </div>
          </div>
          {summary ? (
            <div className="text-sm text-text-muted space-y-1">
              <p>
                Ready to generate: <strong>{unprocessedCount}</strong> worker(s),{' '}
                <strong>{unprocessedHours.toFixed(2)}</strong> hours unprocessed in this period.
              </p>
              {diag && diag.approved_total > 0 && unprocessedCount === 0 ? (
                <p className="text-amber-800 text-xs">
                  {diag.approved_total} approved in the system, but none qualify here
                  {diag.outside_pay_period > 0 ? ` (${diag.outside_pay_period} outside this period)` : ''}
                  {diag.already_on_timesheet > 0 ? ` (${diag.already_on_timesheet} already on a timesheet)` : ''}.
                  {diag.outside_period_clock_out_samples?.length ? (
                    <> Clock-outs: {diag.outside_period_clock_out_samples.join(', ')}.</>
                  ) : null}
                  {' '}Use &quot;Fit to approved&quot; or try Current week.
                </p>
              ) : null}
              {diag && diag.approved_total === 0 ? (
                <p className="text-amber-800 text-xs">
                  No approved attendance yet. Approve clocked-out shifts under Shifts first.
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2" data-tour="timesheets-generate" data-help-section="generate">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPeriodPreset('unprocessed')}
            >
              Fit to approved
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPeriodPreset('current_week')}
            >
              Current week
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => applyPeriodPreset('last_week')}
            >
              Last week
            </Button>
            <Button onClick={handleGenerate} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
              Generate draft timesheets
            </Button>
          </div>
          <p className="text-[11px] text-text-muted pt-1">Locking here is required before Payroll or full invoicing can proceed. Role-standardised entries from Shifts/Organisations keep billing accurate end-to-end.</p>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card data-help-section="lock-export">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-lg flex items-center gap-2">
              All timesheets
              <HelpSectionHint sectionId="lock-export" />
            </CardTitle>
            <select
              className="text-sm border rounded-md px-2 py-1"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="locked">Locked</option>
              <option value="exported">Exported</option>
              <option value="voided">Voided</option>
            </select>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : timesheets.length === 0 ? (
              <p className="text-text-muted text-sm py-4">No timesheets yet. Approve attendance records under Shifts first (ensure billing client + standard role set), then generate here.</p>
            ) : (
              <ul className="space-y-2">
                {timesheets.map((ts) => (
                  <li key={ts.id}>
                    <button
                      type="button"
                      onClick={() => fetchDetail(ts.id)}
                      className={`w-full text-left rounded-lg border p-3 transition-colors ${
                        selectedId === ts.id ? 'border-primary bg-primary/5' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-text-primary">{ts.reference}</p>
                          <p className="text-sm text-text-muted">{ts.employee_name}</p>
                          <p className="text-xs text-text-muted mt-1">
                            {formatDateTime(ts.pay_period_start)} — {formatDateTime(ts.pay_period_end)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <Badge variant="outline" className={STATUS_STYLES[ts.status] || ''}>
                            {ts.status}
                          </Badge>
                          {ts.payroll_batch_id ? (
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px]">
                              On payroll
                            </Badge>
                          ) : null}
                          {ts.status === 'locked' || ts.status === 'exported' ? (
                            <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Contributes to Financially Closed (when invoices match)</Badge>
                          ) : null}
                          <span className="text-xs text-text-muted">
                            {ts.total_shifts} shifts · {ts.total_hours}h
                          </span>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card data-tour="timesheets-detail" data-help-section="lock-export">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              Detail
              <HelpSectionHint sectionId="lock-export" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedId ? (
              <p className="text-sm text-text-muted py-4">Select a timesheet to review lines and actions.</p>
            ) : detailLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : detail ? (
              <div className="space-y-4">
                <div>
                  <p className="font-semibold text-text-primary">{detail.reference}</p>
                  <p className="text-sm text-text-muted">{detail.employee_name}</p>
                  <Badge variant="outline" className={`mt-2 ${STATUS_STYLES[detail.status] || ''}`}>
                    {detail.status}
                  </Badge>
                  {detail.notes ? (
                    <p className="text-sm text-text-muted mt-2">Notes: {detail.notes}</p>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  {detail.status === 'draft' && (
                    <Button size="sm" onClick={handleLock} disabled={actionLoading}>
                      <Lock className="h-4 w-4 mr-1" />
                      Lock
                    </Button>
                  )}
                  {(detail.status === 'locked' || detail.status === 'exported') && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleExport('csv')} disabled={actionLoading}>
                        <Download className="h-4 w-4 mr-1" />
                        Export CSV
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleExport('pdf')} disabled={actionLoading}>
                        <FileText className="h-4 w-4 mr-1" />
                        Export PDF
                      </Button>
                    </>
                  )}
                  {detail.status !== 'voided' && detail.status !== 'exported' && (
                    <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={handleVoid} disabled={actionLoading}>
                      <Ban className="h-4 w-4 mr-1" />
                      Void
                    </Button>
                  )}
                </div>
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50 text-left text-xs uppercase text-slate-500">
                        <th className="py-2 px-2">Payable in / out</th>
                        <th className="py-2 px-2">Hours</th>
                        <th className="py-2 px-2">Location</th>
                        <th className="py-2 px-2">Tier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((item) => (
                        <tr key={item.id} className="border-b last:border-0">
                          <td className="py-2 px-2 whitespace-nowrap">
                            <p>{formatDateTime(item.payable_clock_in_at)}</p>
                            <p className="text-xs text-text-muted">{formatDateTime(item.payable_clock_out_at)}</p>
                          </td>
                          <td className="py-2 px-2">{item.hours_worked}</td>
                          <td className="py-2 px-2 text-xs">{item.location_text || '—'}</td>
                          <td className="py-2 px-2 text-xs">{item.approval_tier || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <p className="text-sm text-text-muted">Could not load detail.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}